// Draws the figures described in diagrams.js as inline SVG. Each figure measures the width of
// its own card and draws at real pixel size, so text never shrinks and a narrow card gets the
// stacked layout instead of a scaled-down wide one.
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { components } from './content';

const PAD = 14;           // margin around a flow, also the lane for loop-back arrows
const GAP_X = 26;         // space between neighbouring nodes
const GAP_Y = 34;         // space between rows, where the arrows run
const LINE = 16;          // line height of a 13px label
const SMALL_LINE = 15;    // line height of 12px text
const CHAR = 7.2;         // estimated width of one 13px character, used for wrapping
const SMALL_CHAR = 6.3;   // the same for 12px text
const DIAMOND_H = 100;
const NARROW_MAX = 440;   // a stacked flow stops growing here and sits centred

// Makes a long formula wrap between its terms: a short bracket, a number with its × or ÷, and a
// coefficient with its symbol each stay on one line. The word joiner stops a break after "|".
export const bind = (expr) => expr
  .replace(/\([^()]{1,20}\)/g, (group) => group.replace(/ /g, '\u2060\u00a0'))
  .replace(/(\d) ([×÷]) /g, '$1\u00a0$2\u00a0')
  .replace(/ ([×÷]) (\d)/g, '\u00a0$1\u00a0$2')
  .replace(/(\d) (?=[A-Z(])/g, '$1\u00a0');

// Width of the element the ref is attached to, kept up to date as the page resizes or zooms.
function useWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    setWidth(Math.floor(ref.current.clientWidth));
    if (!window.ResizeObserver) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

// Greedy word wrap. `room` gives the character limit of each line and repeats its last value.
// A word longer than a line may break after a hyphen, or at a soft hyphen (\u00ad), shown as "-".
function fill(text, room) {
  const lines = [''];
  const limit = () => room[Math.min(lines.length - 1, room.length - 1)];
  text.split(' ').forEach((word) => {
    const whole = word.replace(/\u00ad/g, '');
    const pieces = whole.length <= limit()
      ? [whole]
      : word.replace(/\u00ad/g, '-').match(/[^-]+-?|-/g);
    pieces.forEach((piece, i) => {
      const line = lines[lines.length - 1];
      const longer = line + (line && i === 0 ? ' ' : '') + piece;
      if (line && longer.length > limit()) lines.push(piece);
      else lines[lines.length - 1] = longer;
    });
  });
  return lines;
}

const wrap = (text, px, char = CHAR) => fill(text, [Math.max(4, Math.floor(px / char))]);

// A diamond is widest in the middle, so each line gets its own limit. Tries one line, then two,
// and so on, and keeps the first count where every line fits.
function wrapDiamond(text, w, h) {
  for (let count = 1; count <= 5; count += 1) {
    const room = Array.from({ length: count }, (_, i) => {
      const offset = Math.abs(i - (count - 1) / 2) * LINE + LINE / 2;
      return Math.floor((w * (1 - offset / (h / 2)) - 12) / CHAR);
    });
    const lines = fill(text, room);
    if (lines.length <= count && lines.every((line, i) => line.length <= room[i])) return lines;
  }
  return wrap(text, w * 0.55);
}

// Where a vertical line at x, or a horizontal line at y, crosses the outline of a node.
const half = (n, across, centre, size, other) => (n.kind === 'decision'
  ? (other / 2) * (1 - Math.min(1, Math.abs(across - centre) / (size / 2)))
  : other / 2);
const edgeY = (n, x, dir) => n.cy + dir * half(n, x, n.cx, n.w, n.h);
const edgeX = (n, y, dir) => n.cx + dir * half(n, y, n.cy, n.h, n.w);

// Corner points of the arrow from a to b.
function route(a, b, options, narrow, height) {
  const via = Array.isArray(options.via) ? options.via[narrow ? 1 : 0] : options.via;
  const shift = options.shift || 0;
  const dx = Math.sign(b.cx - a.cx) || 1;
  const dy = Math.sign(b.cy - a.cy) || 1;
  if (via === 'u') {
    return narrow
      ? [[a.cx - a.w / 2, a.cy], [5, a.cy], [5, b.cy], [b.cx - b.w / 2, b.cy]]
      : [[a.cx, a.cy + a.h / 2], [a.cx, height - 6], [b.cx, height - 6], [b.cx, b.cy + b.h / 2]];
  }
  // In line: one straight arrow, drawn through the centre of the smaller of the two.
  if (Math.abs(a.cy - b.cy) < Math.max(a.h, b.h) / 2 - 6) {
    const y = (a.h <= b.h ? a.cy : b.cy) + shift;
    return [[edgeX(a, y, dx), y], [edgeX(b, y, -dx), y]];
  }
  if (Math.abs(a.cx - b.cx) < Math.max(a.w, b.w) / 2 - 6) {
    const x = (a.w <= b.w ? a.cx : b.cx) + shift;
    return [[x, edgeY(a, x, dy)], [x, edgeY(b, x, -dy)]];
  }
  if (via === 'hv') return [[edgeX(a, a.cy, dx), a.cy], [b.cx, a.cy], [b.cx, edgeY(b, b.cx, -dy)]];
  if (via === 'vh') return [[a.cx, edgeY(a, a.cx, dy)], [a.cx, b.cy], [edgeX(b, b.cy, -dx), b.cy]];
  if (via === 'hvh') {
    const end = edgeX(b, b.cy, -dx);
    const mid = end - dx * (GAP_X / 2);
    return [[edgeX(a, a.cy, dx), a.cy], [mid, a.cy], [mid, b.cy], [end, b.cy]];
  }
  // Default: down, across in the gap just before the target row, then down again.
  const end = edgeY(b, b.cx, -dy);
  const mid = end - dy * (GAP_Y / 2);
  return [[a.cx, edgeY(a, a.cx, dy)], [a.cx, mid], [b.cx, mid], [b.cx, end]];
}

// Puts the label on the longest stretch of the arrow. Returns nothing if it would not fit.
function labelSpot(text, points, width) {
  let best = 0;
  for (let i = 1; i < points.length - 1; i += 1) {
    const len = (k) => Math.abs(points[k + 1][0] - points[k][0]) + Math.abs(points[k + 1][1] - points[k][1]);
    if (len(i) > len(best)) best = i;
  }
  const [[x1, y1], [x2, y2]] = [points[best], points[best + 1]];
  const textWidth = text.length * SMALL_CHAR;
  if (y1 === y2) {
    return textWidth > Math.abs(x2 - x1) - 6 ? null : { x: (x1 + x2) / 2, y: y1 - 6, anchor: 'middle' };
  }
  // Beside a vertical stretch, near where it starts, flipped to the left at the right-hand edge.
  // A loop-back lane in the margin of a narrow flow has no room beside it, so it goes unlabelled.
  if (x1 < PAD) return null;
  const y = y1 + Math.sign(y2 - y1) * Math.min(14, Math.abs(y2 - y1) / 2) + 4;
  return x1 + 7 + textWidth > width - 2 ? { x: x1 - 7, y, anchor: 'end' } : { x: x1 + 7, y, anchor: 'start' };
}

// Turns a flow from diagrams.js into pixel positions for a card of the given width.
export function layoutFlow(flow, width) {
  const narrow = width < (flow.wideMin || 720);
  const W = narrow ? Math.min(width, NARROW_MAX) : width;
  const cw = (W - 2 * PAD) / flow.cols[narrow ? 1 : 0];

  const nodes = flow.nodes.map((n) => {
    const [col, row, span = 1] = narrow ? n.atN : n.at;
    const w = span * cw - GAP_X;
    const decision = n.kind === 'decision';
    const lines = decision ? wrapDiamond(n.label, w, DIAMOND_H) : wrap(n.label, w - 16);
    const subLines = n.sub && !decision ? wrap(n.sub, w - 16, SMALL_CHAR) : [];
    const h = decision
      ? DIAMOND_H
      : Math.max(46, lines.length * LINE + subLines.length * SMALL_LINE + (n.kind === 'db' ? 32 : 20));
    return { ...n, row, w, h, lines, subLines, cx: PAD + (col + span / 2) * cw };
  });
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));

  const groups = (flow.groups || []).map((g) => {
    const members = g.members.map((id) => byId[id]);
    const x0 = Math.min(...members.map((n) => n.cx - n.w / 2)) - 9;
    const x1 = Math.max(...members.map((n) => n.cx + n.w / 2)) + 9;
    const lines = wrap(g.label, x1 - x0 - 16, SMALL_CHAR);
    const rows = members.map((n) => n.row);
    return { ...g, members, x0, x1, lines, band: lines.length * SMALL_LINE + 12, first: Math.min(...rows), last: Math.max(...rows) };
  });

  // Rows are as tall as their tallest node, with extra room where a frame label sits.
  const rowCount = 1 + Math.max(...nodes.map((n) => n.row));
  const rowY = [];
  let y = PAD;
  for (let row = 0; row < rowCount; row += 1) {
    const tallest = Math.max(0, ...nodes.filter((n) => n.row === row).map((n) => n.h));
    y += Math.max(0, ...groups.filter((g) => g.first === row).map((g) => (g.foot ? 9 : g.band)));
    rowY[row] = y + tallest / 2;
    y += tallest + Math.max(0, ...groups.filter((g) => g.last === row).map((g) => (g.foot ? g.band : 9))) + GAP_Y;
  }
  nodes.forEach((n) => { n.cy = rowY[n.row]; });
  const loops = flow.edges.some(([, , options]) => options && options.via === 'u');
  const H = y - GAP_Y + PAD + (loops && !narrow ? 14 : 0);

  groups.forEach((g) => {
    const top = Math.min(...g.members.map((n) => n.cy - n.h / 2)) - (g.foot ? 9 : g.band);
    const bottom = Math.max(...g.members.map((n) => n.cy + n.h / 2)) + (g.foot ? g.band : 9);
    Object.assign(g, { cx: (g.x0 + g.x1) / 2, cy: (top + bottom) / 2, w: g.x1 - g.x0, h: bottom - top });
    byId[g.id] = g;
  });

  const edges = flow.edges.map(([from, to, options = {}]) => {
    const points = route(byId[from], byId[to], options, narrow, H);
    const spot = options.label ? labelSpot(options.label, points, W) : null;
    return { key: `${from}-${to}`, points, dashed: options.dashed, label: spot && { ...spot, text: options.label } };
  });

  return { W, H, narrow, nodes, groups, edges };
}

function Shape({ n }) {
  const x = n.cx - n.w / 2;
  const y = n.cy - n.h / 2;
  const cls = `node n-${n.tone || 'shared'}`;
  if (n.kind === 'decision') {
    return <polygon className={cls} points={`${n.cx},${y} ${x + n.w},${n.cy} ${n.cx},${y + n.h} ${x},${n.cy}`} />;
  }
  if (n.kind === 'db') {
    const r = 8;
    return (
      <g className={cls}>
        <path d={`M${x},${y + r} A${n.w / 2},${r} 0 0 1 ${x + n.w},${y + r} V${y + n.h - r} A${n.w / 2},${r} 0 0 1 ${x},${y + n.h - r} Z`} />
        <path className="rim" d={`M${x},${y + r} A${n.w / 2},${r} 0 0 0 ${x + n.w},${y + r}`} />
      </g>
    );
  }
  return <rect className={`${cls}${n.kind === 'end' ? ' end' : ''}`} x={x} y={y} width={n.w} height={n.h} rx={n.kind === 'end' ? 22 : 8} />;
}

function NodeText({ n }) {
  const top = n.cy - (n.lines.length * LINE + n.subLines.length * SMALL_LINE) / 2 + 12 + (n.kind === 'db' ? 6 : 0);
  return (
    <text textAnchor="middle">
      {n.lines.map((line, i) => <tspan key={line + i} className="lbl" x={n.cx} y={top + i * LINE}>{line}</tspan>)}
      {n.subLines.map((line, i) => (
        <tspan key={line + i} className="sub" x={n.cx} y={top + n.lines.length * LINE + i * SMALL_LINE}>{line}</tspan>
      ))}
    </text>
  );
}

function Flow({ flow, width, id, children }) {
  const { W, H, nodes, groups, edges } = layoutFlow(flow, width);
  return (
    <svg className="art" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-labelledby={`${id}t ${id}d`}>
      {children}
      <defs>
        <marker id={`${id}a`} viewBox="0 0 8 8" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path className="arrowhead" d="M0,0 L8,4 L0,8 Z" />
        </marker>
      </defs>
      {groups.map((g) => (
        <g key={g.id}>
          <rect className="frame" x={g.x0} y={g.cy - g.h / 2} width={g.w} height={g.h} rx="12" />
          <text className="sub frame-label">
            {g.lines.map((line, i) => (
              <tspan key={line} x={g.x0 + 10} y={(g.foot ? g.cy + g.h / 2 - g.band + 4 : g.cy - g.h / 2 + 6) + (i + 1) * SMALL_LINE - 3}>{line}</tspan>
            ))}
          </text>
        </g>
      ))}
      {edges.map((e) => (
        <path
          key={e.key} className={e.dashed ? 'edge dashed' : 'edge'} markerEnd={`url(#${id}a)`}
          d={e.points.map(([x, y], i) => `${i ? 'L' : 'M'}${x},${y}`).join(' ')}
        />
      ))}
      {nodes.map((n) => <Shape key={n.id} n={n} />)}
      {nodes.map((n) => <NodeText key={n.id} n={n} />)}
      {edges.filter((e) => e.label).map((e) => (
        <text key={e.key} className="sub edge-label" x={e.label.x} y={e.label.y} textAnchor={e.label.anchor}>{e.label.text}</text>
      ))}
    </svg>
  );
}

// One horizontal bar cut into labelled segments, with optional scale numbers and one marker.
function Bar({ bar, width: W, id, children }) {
  const total = bar.segments.reduce((sum, s) => sum + s.size, 0);
  let before = 0;
  const segments = bar.segments.map((s) => {
    const x = (before / total) * W;
    const w = (s.size / total) * W;
    before += s.size;
    const lines = wrap(s.label, w - 6, SMALL_CHAR);
    const textWidth = Math.max(...lines.map((line) => line.length)) * SMALL_CHAR;
    return { ...s, x, w, lines, labelX: Math.min(Math.max(x + w / 2, textWidth / 2), W - textWidth / 2) };
  });
  const top = Math.max(...segments.map((s) => s.lines.length)) * SMALL_LINE + 6;
  const barH = 34;
  const H = top + barH + (bar.ticks ? 22 : 2) + (bar.mark ? 20 : 0);
  const anchor = (x) => (x < 10 ? 'start' : x > W - 10 ? 'end' : 'middle');
  return (
    <svg className="art" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-labelledby={`${id}t ${id}d`}>
      {children}
      {segments.map((s, i) => (
        <g key={s.label}>
          <rect className={`seg seg-${i % 3}`} x={s.x + 1} y={top} width={s.w - 2} height={barH} rx="6" />
          <text className="sub" textAnchor="middle">
            {s.lines.map((line, j) => <tspan key={line} x={s.labelX} y={top - 8 - (s.lines.length - 1 - j) * SMALL_LINE}>{line}</tspan>)}
          </text>
          {s.value && <text className="lbl" textAnchor="middle" x={s.x + s.w / 2} y={top + barH / 2 + 5}>{s.value}</text>}
        </g>
      ))}
      {bar.ticks && bar.ticks.map((t) => {
        const x = (t / total) * W;
        return <text key={t} className="sub" x={x} y={top + barH + 16} textAnchor={anchor(x)}>{t}</text>;
      })}
      {bar.mark && (
        <>
          <line className="edge dashed" x1={(bar.mark.at / total) * W} x2={(bar.mark.at / total) * W} y1={top - 4} y2={top + barH + 4} />
          <text className="sub" x={W} y={top + barH + 36} textAnchor="end">{bar.mark.label}</text>
        </>
      )}
    </svg>
  );
}

// Grouped horizontal bars: each row has a name, a note and one bar per value.
function Chart({ chart, width: W, id, children }) {
  const room = W - 58;
  const parts = [];
  let y = 0;
  const say = (text, cls, gap) => wrap(text, W, cls === 'lbl' ? CHAR : SMALL_CHAR).forEach((line, i) => {
    y += i ? SMALL_LINE : gap;
    parts.push(<text key={`${text}${i}${y}`} className={cls} x="0" y={y}>{line}</text>);
  });
  chart.rows.forEach((row) => {
    say(row.name, 'lbl', 16);
    say(row.note, 'sub', 16);
    row.bars.forEach((b, i) => {
      say(`${b.kind}: ${b.name}`, 'sub', 22);
      const w = (b.value / chart.max) * room;
      parts.push(<rect key={`${row.name}${b.kind}`} className={`seg seg-${i ? 0 : 2}`} x="1" y={y + 6} width={w} height="14" rx="4" />);
      parts.push(<text key={`${row.name}${b.kind}v`} className="lbl" x={w + 8} y={y + 18}>{b.shown}</text>);
      y += 20;
    });
    y += 20;
  });
  return (
    <svg className="art" width={W} height={y - 14} viewBox={`0 0 ${W} ${y - 14}`} role="img" aria-labelledby={`${id}t ${id}d`}>
      {children}
      {parts}
    </svg>
  );
}

const toneNames = Object.fromEntries(components.map((c) => [c.id, c.short]));
const shapeNames = [['decision', 'Decision'], ['db', 'Database'], ['end', 'Result']];

// Says in words what each colour and shape in a flow means.
function Legend({ flow }) {
  const uses = (key, value) => flow.nodes.some((n) => n[key] === value);
  return (
    <ul className="legend">
      {components.filter((c) => uses('tone', c.id)).map((c) => (
        <li key={c.id}><span className={`swatch n-${c.id}`} />{toneNames[c.id]}</li>
      ))}
      <li><span className="swatch n-shared" />Step</li>
      {shapeNames.filter(([kind]) => uses('kind', kind)).map(([kind, name]) => (
        <li key={kind}><span className={`swatch n-shared k-${kind}`} />{name}</li>
      ))}
      {flow.dashed && <li><span className="swatch k-dashed" />{flow.dashed}</li>}
    </ul>
  );
}

// One card: title, caption, the drawing, its legend and a text version.
function Figure({ figure }) {
  const [ref, width] = useWidth();
  const id = useId().replace(/:/g, '');
  const Art = (figure.flow && Flow) || (figure.bar && Bar) || Chart;
  return (
    <div className="method figure">
      <h4>{figure.title}</h4>
      <p>{figure.caption}</p>
      <div className={figure.list ? 'figure-split' : undefined}>
        <div ref={ref} className="figure-art">
          {width > 0 && (
            <Art flow={figure.flow} bar={figure.bar} chart={figure.chart} width={width} id={id}>
              <title id={`${id}t`}>{figure.title}</title>
              <desc id={`${id}d`}>{figure.caption}</desc>
            </Art>
          )}
        </div>
        {figure.list && (
          <div>
            <p><strong>{figure.listTitle}</strong></p>
            <ol className="steps">
              {figure.list.map(([name, detail]) => <li key={name}><strong>{name}:</strong> {detail}</li>)}
            </ol>
          </div>
        )}
      </div>
      {figure.expr && <p className="expr">{bind(figure.expr)}</p>}
      {figure.flow && <Legend flow={figure.flow} />}
      <details>
        <summary>Text version</summary>
        {Array.isArray(figure.alt)
          ? <ol className="steps">{figure.alt.map((line) => <li key={line}>{line}</li>)}</ol>
          : <p>{figure.alt}</p>}
      </details>
    </div>
  );
}

export default Figure;
